import { useGetAllClientMarketFixConnectionsInfinite } from '@/hooks/query/useGetAllClientMarketFixConnectionsInfinite.ts'
import { useNavigate } from '@tanstack/react-router'
import { ChevronRightIcon } from 'lucide-react'

import { useGetClientMarket } from '@/gen/publicApi'

import { UnexpectedDataStateError } from '@/containers/unexpectedDataStateError'

import { Card } from '@/components/card'
import LoadMoreButton from '@/components/loadMoreButton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/table'

import { useSetBreadcrumbs } from '@/state/breadcrumbs'
import { useConnectionFilter } from '@/state/connectionFilter'

import { cn } from '@/utils/cn'

import ConnectionTableFilter from './connectionTableFilter'

interface FixSessionsTableProps {
    market_code: string
    client_code: string
}

export function FixSessionsTable({ market_code, client_code }: FixSessionsTableProps) {
    const { data: market } = useGetClientMarket(client_code, market_code, { query: { suspense: true } })
    const connectionFilter = useConnectionFilter(market_code)
    const {
        data: marketFixConnections,
        fetchNextPage,
        hasNextPage,
        isLoading,
    } = useGetAllClientMarketFixConnectionsInfinite(client_code, market_code, connectionFilter.includeDeleted)

    const navigate = useNavigate()
    useSetBreadcrumbs([
        {
            to: '/$client_code/markets',
            text: 'Markets',
            params: {
                client_code,
            },
        },
        'seperator',
        {
            to: '/$client_code/markets/$market_code',
            params: {
                market_code,
                client_code,
            },
            text: market?.name || '--',
        },
        'seperator',
        {
            to: '/$client_code/markets/$market_code/connections',
            params: {
                market_code,
                client_code,
            },
            text: 'Connections',
        },
    ])

    const onRowClick = (connectionId: string) => {
        navigate({
            to: `/$client_code/markets/$market_code/connections/fix/$session_id`,
            params: {
                client_code,
                market_code,
                session_id: connectionId,
            },
        })
    }

    if (!marketFixConnections) {
        return <UnexpectedDataStateError />
    }

    const normalisedFilterText = (connectionFilter.filterText || '').toLowerCase()
    const filteredData = normalisedFilterText.length
        ? marketFixConnections?.filter(
              c =>
                  c.senderCompId.toLowerCase().includes(normalisedFilterText) ||
                  c.targetCompId.toLowerCase().includes(normalisedFilterText) ||
                  c.username.toLowerCase().includes(normalisedFilterText),
          )
        : marketFixConnections

    return (
        <Card className="flex-grow" hideOverflow>
            <Table>
                <TableHeader>
                    <ConnectionTableFilter marketCode={market_code} />
                    <TableRow>
                        <TableHead>SenderCompId</TableHead>
                        <TableHead>TargetCompId</TableHead>
                        <TableHead>BeginString</TableHead>
                        <TableHead>DefaultAppVerId</TableHead>
                        <TableHead>TransportDataDictionary</TableHead>
                        <TableHead />
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {filteredData.map(connection => (
                        <TableRow
                            key={connection.id}
                            onClick={() => onRowClick(connection.id)}
                            className={cn('cursor-pointer', {
                                'text-destructive': connection.rowState === 'Deleted',
                            })}
                        >
                            <TableCell>{connection.senderCompId}</TableCell>
                            <TableCell>{connection.targetCompId}</TableCell>
                            <TableCell>{connection.beginString}</TableCell>
                            <TableCell>{connection.defaultAppVerId}</TableCell>
                            <TableCell>{connection.transportDataDictionary}</TableCell>
                            <TableCell className="flex justify-end">
                                <ChevronRightIcon />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <div className="flex justify-center">
                <LoadMoreButton
                    className="my-4"
                    fetchNextPage={fetchNextPage}
                    isLoading={isLoading}
                    hasNextPage={hasNextPage}
                />
            </div>
        </Card>
    )
}
